/**
 * Sentence segmentation shared by every surface that shows, cites, trims or
 * classifies prose one sentence at a time.
 *
 * Boundaries come from `Intl.Segmenter` (Unicode UAX #29 sentence rules), which
 * already keeps decimals ("3.5%", "$1.2B"), versions ("1.2.3") and periods
 * followed by lowercase ("e.g. a hammer", "U.S. economy") inside a sentence.
 * UAX #29 still breaks after a period followed by an uppercase word, so
 * "Mr. Smith", "Dr. Jones", "Jan. 5" and "The U.S. GDP" are rejoined here.
 * A conservative regex covers runtimes without `Intl.Segmenter`.
 */

export type SentenceSpan = {
  /** The sentence exactly as written in the input, without edge whitespace. */
  text: string;
  /** Offset of the first character of `text` in the input. */
  start: number;
  /** Offset just past the last character of `text` in the input. */
  end: number;
};

export type SentenceOptions = {
  /** BCP 47 locale for the segmenter. Defaults to `"en"` for deterministic output. */
  locale?: string;
};

type SentenceSegmenter = { segment: (input: string) => Iterable<{ segment: string; index: number }> };
type SentenceSegmenterConstructor = new (
  locale: string,
  options: { granularity: "sentence" },
) => SentenceSegmenter;

const segmenters = new Map<string, SentenceSegmenter>();

function segmenterFor(locale: string): SentenceSegmenter | null {
  const Segmenter = (Intl as unknown as { Segmenter?: SentenceSegmenterConstructor }).Segmenter;
  if (typeof Segmenter !== "function") return null;
  let segmenter = segmenters.get(locale);
  if (!segmenter) {
    segmenter = new Segmenter(locale, { granularity: "sentence" });
    segmenters.set(locale, segmenter);
  }
  return segmenter;
}

// Without Intl.Segmenter: break after terminal punctuation and space unless
// lowercase follows, after CJK full stops, and at line breaks. The lookahead
// also refuses whitespace so `\s+` cannot backtrack to stop before a second
// space ("End.  lower case").
const FALLBACK_BOUNDARY = /[.!?\u2026]+["'\u201d\u2019)\]]*\s+(?![\s\p{Ll}])|[\u3002\uff01\uff1f]+["'\u201d\u2019)\]\u300d\u300f]*\s*|\n+/gu;

function rawBoundaries(text: string, locale: string): number[] {
  const segmenter = segmenterFor(locale);
  const ends: number[] = [];
  if (segmenter) {
    for (const { segment, index } of segmenter.segment(text)) ends.push(index + segment.length);
    return ends;
  }
  for (const match of text.matchAll(FALLBACK_BOUNDARY)) {
    const end = (match.index ?? 0) + match[0].length;
    if (end < text.length) ends.push(end);
  }
  ends.push(text.length);
  return ends;
}

// Every rule below reads only the last few characters before a break and the
// first few after it, so merging stays linear however many breaks rejoin.
const TAIL_CHARS = 24;
const HEAD_CHARS = 32;
// Letters or digits directly before an abbreviation make it part of a longer
// token ("1st.", "Disco."), not an abbreviation.
const BEFORE = "(?:^|[^\\p{L}\\p{N}])";

// A period after these never ends a sentence in practice.
const ALWAYS_CONTINUES = new RegExp(
  `${BEFORE}(?:mr|mrs|ms|mx|dr|prof|gen|col|lt|capt|sgt|rep|sen|gov|rev|hon|messrs|mme|mlle|e\\.g|i\\.e|cf|vs|viz|approx|incl|al)\\.$`,
  "iu",
);
// A period after these continues when a number follows ("No. 5", "Jan. 5", "p. 12").
const CONTINUES_BEFORE_NUMBER = new RegExp(
  `${BEFORE}(?:no|nos|nr|nro|num|ref|inv|fig|figs|vol|vols|p|pp|ch|sec|art|ca|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\\.$`,
  "iu",
);
// Initialisms ("U.S.", "U.K.") and company or address suffixes continue
// unless the next word plainly opens a new sentence.
const INITIALISM = new RegExp(`(?:^|[^\\p{L}\\p{N}.])(?:\\p{L}\\.){2,}$`, "u");
const SUFFIX = new RegExp(`${BEFORE}(?:inc|ltd|corp|co|llc|plc|bros|jr|sr|st|ave|blvd|mt|ft)\\.$`, "iu");
// A single capital initial is a name ("J. Smith", "John F. Kennedy") or a
// label ("plan B.", "vitamin C."). Names win when the initial opens the text,
// follows a capitalized word, another initial or a comma, or follows a word
// that introduces a person; a label after any other lowercase word ends the
// sentence. Splitting "met J. Smith" would break far more prose than keeping
// a rare "plan B. Prices rose" whole, so the person-introducing list stays.
const SINGLE_INITIAL = /(?:^|[^\p{L}\p{N}.])\p{Lu}\.$/u;
const WORD_BEFORE_INITIAL = /(?:^|[^\p{L}\p{N}])(\p{L}+)(\.?,?)\s+\p{Lu}\.$/u;
const NAME_LEADS = new Set([
  "by", "with", "to", "from", "and", "of", "for", "at", "said", "says", "told", "met", "dear", "cc", "per",
  "via", "against", "versus", "thanks", "ask", "call", "email", "invite", "like", "or", "nor", "than",
]);
const SENTENCE_OPENERS = new Set([
  "a", "an", "the", "this", "that", "these", "those", "it", "its", "they", "their", "there", "here",
  "we", "our", "my", "he", "his", "she", "her", "i", "you", "your", "but", "and", "so", "yet", "however",
  "meanwhile", "then", "next", "now", "today", "also", "still", "in", "on", "at", "for", "after",
  "before", "when", "while", "if", "as", "by", "with", "from", "what", "why", "how", "who", "where",
  "which", "some", "someone", "something", "every", "everyone", "everybody", "everything", "many",
  "most", "all", "both", "each", "no", "none", "nobody", "one",
]);
const LEADING_PUNCTUATION = /^["'\u201c\u2018(\[]+/u;
const FIRST_WORD = /^\p{L}+/u;

function singleInitialIsName(tail: string): boolean {
  const before = WORD_BEFORE_INITIAL.exec(tail);
  if (!before) return true;
  const [, word, punctuation] = before;
  if (punctuation) return true;
  if (word !== word.toLocaleLowerCase("en")) return true;
  return NAME_LEADS.has(word);
}

/** Whether a break after `tail` (the previous text's end) rejoins `head` (the next text's start). */
function continuesInto(tail: string, head: string): boolean {
  if (ALWAYS_CONTINUES.test(tail)) return true;
  const opening = head.replace(LEADING_PUNCTUATION, "");
  if (CONTINUES_BEFORE_NUMBER.test(tail) && /^[#\d]/.test(opening)) return true;
  const singleInitial = SINGLE_INITIAL.test(tail);
  if (singleInitial && !singleInitialIsName(tail)) return false;
  if (!singleInitial && !INITIALISM.test(tail) && !SUFFIX.test(tail)) return false;
  const word = opening.match(FIRST_WORD)?.[0];
  return !word || !SENTENCE_OPENERS.has(word.toLocaleLowerCase("en"));
}

/** The sentences of `text` with their offsets, abbreviation breaks rejoined. */
export function sentenceSpans(text: string, options: SentenceOptions = {}): SentenceSpan[] {
  if (!text) return [];
  const bounds: Array<{ start: number; end: number }> = [];
  let start = 0;
  for (const end of rawBoundaries(text, options.locale ?? "en")) {
    let spanStart = start;
    let spanEnd = end;
    start = end;
    while (spanStart < spanEnd && /\s/.test(text[spanStart])) spanStart += 1;
    while (spanEnd > spanStart && /\s/.test(text[spanEnd - 1])) spanEnd -= 1;
    if (spanStart === spanEnd) continue;
    const previous = bounds[bounds.length - 1];
    if (previous) {
      // A paragraph break always ends a sentence, abbreviation or not.
      const separatedByLine = text.slice(previous.end, spanStart).includes("\n");
      const tail = text.slice(Math.max(previous.start, previous.end - TAIL_CHARS), previous.end);
      const head = text.slice(spanStart, Math.min(spanEnd, spanStart + HEAD_CHARS));
      if (!separatedByLine && continuesInto(tail, head)) {
        previous.end = spanEnd;
        continue;
      }
    }
    bounds.push({ start: spanStart, end: spanEnd });
  }
  return bounds.map(({ start: from, end: to }) => ({ text: text.slice(from, to), start: from, end: to }));
}

/** The sentences of `text`, trimmed, as written. */
export function splitSentences(text: string, options: SentenceOptions = {}): string[] {
  return sentenceSpans(text, options).map((span) => span.text);
}

/**
 * The leading sentences of `text` until they reach `minLength` characters,
 * or null when the text is empty. Keeps headline fragments ("Done.") from
 * standing alone when a caller needs a meaningful first line.
 */
export function leadingSentences(text: string, minLength = 0, options: SentenceOptions = {}): string | null {
  const spans = sentenceSpans(text, options);
  if (spans.length === 0) return null;
  const first = spans[0];
  let end = first.end;
  for (let index = 1; index < spans.length && end - first.start < minLength; index += 1) end = spans[index].end;
  return text.slice(first.start, end);
}
